import { AutoSaveIndicator } from '@dealstack/frontend/app/pages/Valuations/components/AutoSaveIndicator/AutoSaveIndicator';
import {
	formatMoicMetric,
	formatPercentage,
	formatValueInCurrency,
} from '@dealstack/frontend/app/pages/Valuations/components/table/utils/formatting';
import { ValuationBanner } from '@dealstack/frontend/app/pages/Valuations/components/ValuationMatricsBanner/ValuationMatricsBanner';
import { LinearSolveWarning } from '@dealstack/frontend/app/pages/Valuations/components/LinearSolveWarning/LinearSolveWarning';
import { useValuationsQuery } from '@dealstack/frontend/app/pages/Valuations/queries/useValuationsQuery';
import { getNumberFactor } from '@dealstack/frontend/app/pages/Valuations/shared/numberFactor';
import { useCalculationWorkflow } from '@dealstack/frontend/app/pages/Valuations/queries/useCalculationWorkflow';
import { useValuationBreadcrumbs } from '@dealstack/frontend/app/pages/Valuations/hooks/useValuationBreadcrumbs';
import { useValuationNavigation } from '@dealstack/frontend/app/pages/Valuations/hooks/useValuationNavigation';
import { ValuationLocation } from '@dealstack/frontend/app/pages/Valuations/shared/valuationRoutes';
import {
	ComputeProgressLabel,
	TitleContainer,
	ValuationsLayout,
	ValuationsLoaderContainer,
} from '@dealstack/frontend/app/pages/Valuations/styled';
import { InPageNavValuationsHeaderContainer } from '@dealstack/frontend/app/pages/Valuations/views/ValuationsHome/styled';
import { ByPersonValuationTableWithData as NewByPersonValuationTable } from '@dealstack/frontend/app/pages/Valuations/views/ValuationSummary/ByPersonValuationTable/ByPersonValuationTable';
import { GlobalValuationTableWithData } from '@dealstack/frontend/app/pages/Valuations/views/ValuationSummary/GlobalViewTable/GlobalViewValuationTable';
import {
	ContentWrapper,
	ControlsContainer,
	LeftContainer,
	RightContainer,
	TableContainer,
	TableSwitch,
	ValuationDateContainer,
	ValuationDateLabel,
	ValuationDateValue,
	ValuationsSummaryContainer,
} from '@dealstack/frontend/app/pages/Valuations/views/ValuationSummary/styled';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { downloadValuationExcel } from '@dealstack/frontend/shared/dataExchange/services/valuations.service';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook';
import {
	useTranslationDefault,
	useTranslationValuations,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';
import { HighPrecisionDecimal } from '@dealstack/shared/lib/format/currency/highPrecisionDecimal';
import { ValuationNumberFormat } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS, VALUATION_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { Button, Flex, Text } from '@mantine/core';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BannerColumn } from '@dealstack/frontend/app/pages/Valuations/components/ValuationMatricsBanner/ValuationMatricsBanner.types';
import { ValuationsWarnings } from '@dealstack/frontend/app/pages/Valuations/components/ValuationsWarnings/ValuationsWarnings';

export const ValuationSummary = () => {
	const { dealId, pathValuationId, navigateTo } = useValuationNavigation();
	const { valuations } = useValuationsQuery(dealId);
	const currentValuation = valuations.find((v) => v.id === pathValuationId);
	const currentNumberFactor = getNumberFactor(currentValuation?.defaultNumberFormat);

	const {
		isSummaryLoading,
		isSummaryError,
		computeProgress,
		summaryByPerson: summaryByPersonData,
		summaryByMip: summaryByMipData,
		resultId,
		linearSolve,
		invalidateValuations,
		validationErrors,
		reloadSummary,
	} = useCalculationWorkflow(dealId, pathValuationId);

	const { valuationId } = useParams();
	const tValuations = useTranslationValuations();
	const tDefault = useTranslationDefault();
	const { value: inPageFeatureFlag } = useDealstackFeatureFlag(
		FEATURE_FLAG.IN_PAGE_BREADCRUMBS_AND_QUICK_SWITCH_MENU
	);

	useValuationBreadcrumbs(
		{
			view: 'summary',
			dealId: dealId ?? '',
			valuation: currentValuation ?? undefined,
		},
		inPageFeatureFlag
	);

	useEffect(() => {
		if (summaryByPersonData) {
			invalidateValuations();
		}
	}, [summaryByPersonData, invalidateValuations]);

	const [viewMode, setViewMode] = useState<'global' | 'byPerson'>('global');
	const [showFilterInputs, setShowFilterInputs] = useState(false);

	if (!currentValuation || isSummaryLoading) {
		// Live progress fed by the compute SSE stream. Always shows the current phase; while the
		// waterfall is computing it also shows the engine's live pour count. Absent (null) -> spinner.
		let progressText: string | null = null;
		if (computeProgress) {
			const stageLabel = tValuations(`summary.compute_progress.${computeProgress.stage}`, {
				defaultValue: {
					queued: 'Waiting for an open compute slot...',
					'loading-data': 'Loading deal data...',
					computing: 'Computing waterfall...',
					persisting: 'Saving results...',
				}[computeProgress.stage],
			});
			// While the engine is pouring, append its live iteration count to the stage label.
			progressText =
				computeProgress.stage === 'computing' && computeProgress.iterations > 0
					? tValuations('summary.compute_progress.computing_iterations', {
							defaultValue: '{{stage}} ({{iterations}} iterations)',
							stage: stageLabel,
							iterations: computeProgress.iterations,
						})
					: stageLabel;
		}
		return (
			<ValuationsLoaderContainer>
				<InlineLoader withoutBackground />
				{progressText && <ComputeProgressLabel>{progressText}</ComputeProgressLabel>}
			</ValuationsLoaderContainer>
		);
	}

	// Compute/result failed (after any in-flight retry has resolved the loader above): show an in-page
	// error + retry rather than dropping through to an empty summary. The global toast still fires via
	// QueryCache.onError; this just gives the user a recovery affordance. WAT-730.
	if (isSummaryError) {
		return (
			<ValuationsLoaderContainer>
				<ComputeProgressLabel>
					{tValuations('summary.compute_failed', {
						defaultValue: 'We could not compute this valuation. Please try again.',
					})}
				</ComputeProgressLabel>
				<Button variant="default" size="lg" onClick={() => reloadSummary()}>
					{tValuations('summary.compute_failed_retry', { defaultValue: 'Retry' })}
				</Button>
			</ValuationsLoaderContainer>
		);
	}

	const defaultNumberFormat = currentValuation.defaultNumberFormat;
	const multipleLabel =
		defaultNumberFormat === ValuationNumberFormat.UNFORMATTED
			? ''
			: tValuations(`multiples.${defaultNumberFormat.toLocaleLowerCase('en-GB')}_unit`);

	const bannerColumns: BannerColumn[] = [
		{
			label: tValuations('target_nodes.total_equity_value_title'),
			value: formatValueInCurrency(
				new HighPrecisionDecimal(currentValuation.totalEquityValue).div(
					currentNumberFactor
				),
				currentValuation.defaultCurrency,
				multipleLabel
			),
		},
	];

	if (
		currentValuation.totalEnterpriseValue &&
		!new HighPrecisionDecimal(currentValuation.totalEnterpriseValue).equals(
			new HighPrecisionDecimal(0)
		)
	) {
		bannerColumns.push({
			label: tValuations('deal_card.total_enterprise_value'),
			value: formatValueInCurrency(
				new HighPrecisionDecimal(currentValuation.totalEnterpriseValue).div(
					currentNumberFactor
				),
				currentValuation.defaultCurrency,
				multipleLabel
			),
		});
	}
	const metrics: string[] = [];

	if (currentValuation?.moic) {
		const hpMoic = new HighPrecisionDecimal(currentValuation.moic);
		metrics.push(`MOIC ${formatMoicMetric(hpMoic)}`);
	}
	if (currentValuation?.xirr) {
		const hpXIRR = new HighPrecisionDecimal(currentValuation.xirr);
		metrics.push(`xIRR ${formatPercentage(hpXIRR)}`);
	}

	if (metrics.length > 0) {
		bannerColumns.push({
			label: tValuations('deal_card.returns_to_sponsor'),
			value: metrics.join(', '),
		});
	}

	const personId = summaryByPersonData?.persons?.[0]?.id;
	const mipId = summaryByPersonData?.persons?.[0]?.groups?.[0]?.id;

	return (
		<ValuationsLayout>
			<InPageNavValuationsHeaderContainer>
				<TitleContainer>
					<Text data-testid={VALUATION_ELEMENT_ID.VALUATION_SUMMARY_TITLE} size="B3Bold">
						{currentValuation?.name}
					</Text>
					<AutoSaveIndicator />
				</TitleContainer>

				<Flex align="center" gap="xs">
					<Button
						variant="default"
						size="lg"
						data-testid={VALUATION_ELEMENT_ID.VALUATION_SUMMARY_DOWNLOAD}
						disabled={isSummaryLoading || summaryByPersonData === undefined}
						onClick={() =>
							downloadValuationExcel({
								valuationId: currentValuation?.id ?? '',
								dealId: dealId ?? '',
								resultId: resultId ?? '',
								tDefault,
							})
						}
					>
						<Flex gap="xs" align="center">
							<Text c="dark.8" size="B6Bold">
								Download
							</Text>
							<Icon
								variant="download"
								size="xl"
								color={defaultTheme.v3.colors.dark[8]}
							/>
						</Flex>
					</Button>
					<Button
						variant="default"
						size="lg"
						data-testid={VALUATION_ELEMENT_ID.VALUATION_SUMMARY_EDIT_VALUATION_BTN}
						onClick={() =>
							navigateTo(ValuationLocation.SPECIFIC, {
								dealId: dealId ?? '',
								valuationId: currentValuation?.id ?? '',
							})
						}
					>
						<Flex gap="xs" align="center">
							<Text c="dark.8" size="B6Bold">
								{tValuations('summary.edit_valuation_button')}
							</Text>
							<Icon
								variant="pencil-edit-line"
								color={defaultTheme.v3.colors.dark[8]}
								size="xl"
							/>
						</Flex>
					</Button>
					<ValuationsWarnings
						validationErrors={validationErrors}
						isSummaryLoading={isSummaryLoading}
					/>
				</Flex>
			</InPageNavValuationsHeaderContainer>
			<ValuationsSummaryContainer data-testid="ValuationsSummaryContainer">
				<LinearSolveWarning status={linearSolve} />
				<ValuationBanner columns={bannerColumns} />
				<ContentWrapper>
					<ControlsContainer>
						<LeftContainer>
							<TableSwitch
								value={viewMode}
								onChange={(value) => setViewMode(value as 'global' | 'byPerson')}
								data={[
									{
										label: (
											<div
												data-testid={
													TEST_ELEMENT_IDS.VALUATION_SUMMARY_GLOBAL_VIEW_TOGGLE
												}
											>
												{tValuations('summary.global.tab_name')}
											</div>
										),
										value: 'global',
									},
									{
										label: (
											<div
												data-testid={
													TEST_ELEMENT_IDS.VALUATION_SUMMARY_BY_PERSON_TOGGLE
												}
											>
												{tValuations('summary.by_person.tab_name')}
											</div>
										),
										value: 'byPerson',
									},
								]}
							/>
							<ValuationDateContainer>
								<ValuationDateLabel variant="sans/14/400">
									{tValuations('summary.valuation_date_label')}
								</ValuationDateLabel>
								<ValuationDateValue
									data-testid={VALUATION_ELEMENT_ID.SUMMARY_TABLE_VALUATION_DATE}
									variant="sans/14/400"
								>
									{DateTime.fromISO(currentValuation.valuationDate, {
										zone: 'utc',
									}).toFormat('dd/MM/yyyy')}
								</ValuationDateValue>
							</ValuationDateContainer>
						</LeftContainer>

						<RightContainer>
							{viewMode === 'byPerson' && (
								<Button
									variant="default"
									data-testid={VALUATION_ELEMENT_ID.PERSON_FILTER_TABLE_BTN}
									onClick={() => setShowFilterInputs(!showFilterInputs)}
									sx={
										showFilterInputs
											? {
													backgroundColor: defaultTheme.v3.colors.blue[0],
													border: `1px solid ${defaultTheme.v3.colors.blue[4]}`,
												}
											: undefined
									}
								>
									<Flex gap="xs" align="center">
										<Icon
											variant="filter-v3"
											size="vlg"
											color={
												showFilterInputs
													? defaultTheme.v3.colors.blue[6]
													: defaultTheme.v3.colors.dark[9]
											}
										/>
										<Text
											size="B6Medium"
											c={
												showFilterInputs
													? defaultTheme.v3.colors.blue[6]
													: defaultTheme.v3.colors.dark[9]
											}
										>
											{tValuations('summary.by_person.filter_button')}
										</Text>
									</Flex>
								</Button>
							)}
							<Button
								data-testid={VALUATION_ELEMENT_ID.SOURCES_AND_USES}
								variant="default"
								onClick={() =>
									navigateTo(ValuationLocation.SOURCES_AND_USES, {
										dealId: dealId ?? '',
										valuationId: valuationId ?? '',
										personId,
										mipId: mipId ?? undefined,
									})
								}
								disabled={!personId}
							>
								<Text size="B6Medium" c="dark.9">
									{tValuations('summary.sources_and_uses_button')}
								</Text>
							</Button>
						</RightContainer>
					</ControlsContainer>
					<TableContainer data-testid={VALUATION_ELEMENT_ID.SUMMARY_TABLE}>
						{viewMode === 'global' && (
							<GlobalValuationTableWithData summaryByMipData={summaryByMipData} />
						)}
						{viewMode === 'byPerson' && (
							<NewByPersonValuationTable
								showFilterInputs={showFilterInputs}
								summaryByPersonData={summaryByPersonData}
							/>
						)}
					</TableContainer>
				</ContentWrapper>
			</ValuationsSummaryContainer>
		</ValuationsLayout>
	);
};
